import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {Card, Col, Row, Space, Table, Typography} from 'antd';
import Button from '@mui/material/Button';
import {DeleteForeverRounded} from "@mui/icons-material";
import useEtfMatrix from "../EtfMatrix/useEtfMatrix";
import { useEnsureTickersLoaded } from "../../hooks/useEnsureTickersLoaded";
import { fetchTickerData } from "../../api/tickersApi";
import {selectAllTickerAttrs} from "../../store/slices/tickers.slice";
import {mergeEtfs} from "../SmaV2/utils/smaUtils";
import TickerChip from "../../components/TickerChip";
import {stringMixin} from "../../utils/strings";


const TickerColumn = ({stockList})=>{
    const sortedStocks = stockList.sort((a, b) => b.wgt - a.wgt);
    const topStocks = sortedStocks.length > 50 ? sortedStocks.slice(0, 50) : sortedStocks;
    const { tickers } = useEnsureTickersLoaded(topStocks.map(x=>x.symbol));
    const columns = [
        {
            title: "Ticker",
            dataIndex: "symbol",
            key: "symbol",
            render: (symbol) => {
                const ticker = tickers.find(x => x.symbol === symbol.toLowerCase());
                return ticker ? <TickerChip ticker={ticker} /> : symbol;
            }
        },
        {
            title: "Weight",
            dataIndex: "wgt",
            key: "wgt",
            render: (wgt) => stringMixin.pct(wgt)
        }
    ]

    return (
        <Table dataSource={topStocks} columns={columns} pagination={false} />
    )


}

const getTop50StocksFromEtfPortfolio = (portfolio, etfData)=>{
    const newEtfData = [];
    for (let k in portfolio){
        const weight = portfolio[k].wgt;
        if (weight > 0 && etfData.hasOwnProperty(k)){
            const data = etfData[k];
            if (data.hasOwnProperty('holdings') && Array.isArray(data.holdings)) {
                newEtfData.push({...data, weight});
            }
        }
    }
    const mergedData = mergeEtfs(Object.values(newEtfData));
    return mergedData;
}


/**
 *
 * @param portfolios {Array[Object]} key, name, ptfl: {Object}, extra: {JSX.Element}
 * @param onRemove {Function, optional} Callback to remove a portfolio
 * @param wrap {Boolean, optional} Whether to wrap the row
 * @returns {JSX.Element}
 * @constructor
 */
const PortfolioXray = ({ portfolios, onRemove, wrap=false }) => {
    // Step 1: Use `useEnsureTickersLoaded` to load the tickers initially.
    const uniqueTickers = [...new Set(portfolios.map(portfolio => Object.keys(portfolio.ptfl)).flat())];
    const { tickers } = useEnsureTickersLoaded(uniqueTickers);
    const dispatch = useDispatch();
    const etfs = tickers && tickers.filter(ticker => ticker.type === 'etf');
    const [fetched, setFetched] = useState(false);
    const etfData = useSelector(state=>selectAllTickerAttrs(state, etfs.map(x=>x.symbol), ['symbol', 'name', 'holdings']));

    useEffect(() => {
        if (etfs && etfs.length > 0) {
            dispatch(fetchTickerData({symbols: etfs.map(x=>x.symbol), fields: ['symbol', 'name', 'holdings']})).then((resp)=>{
                setFetched(true);
            })
        }
    }, [dispatch])// Inital effect to fetch ETF data


    const mergedPortfoliosData =  fetched && etfData && portfolios.map(portfolio => {
        const stocks = getTop50StocksFromEtfPortfolio(portfolio.ptfl, etfData);
        return {
            ...portfolio,
            stocks
        }
    });
    const isReady = mergedPortfoliosData && Array.isArray(mergedPortfoliosData) && mergedPortfoliosData.some(x => x.stocks.length > 0);

    return (
        <Row className={'bordered-rows'} wrap={wrap}>
            { isReady && mergedPortfoliosData.filter(x=>x.stocks.length>0).map((portfolio, index) => (
                <Col>
                    <Card bordered={false}>
                        <Row className={'bordered round-1 p-3'} justify={'space-between'} align={'middle'}>
                            <Typography.Text strong>
                                {portfolio.name}
                            </Typography.Text>
                            {onRemove && (
                                <Button variant={'outlined'} onClick={()=>onRemove(portfolio?.key)}><DeleteForeverRounded/></Button>
                            )}
                        </Row>
                        <Row className={'p-3'}>
                            {portfolio?.extra}
                        </Row>
                        <TickerColumn key={index} stockList={portfolio.stocks} />

                    </Card>
                </Col>
            ))}
        </Row>
    );
};

export default PortfolioXray;
